import { useGetApprovalAllType } from '@/hooks/apiHooks';

import { FormProvider, useForm } from 'react-hook-form';
import { useDebounce } from 'usehooks-ts';
import SubHeaderContent from '@/components/MobileApproval/SubHeaderContent';
import React, { useEffect, useMemo, useState } from 'react';
import CustomInput from '@/components/Organization/CustomInput';
import NoData from '@/components/Approval/@components/NoData';
import { categorizeByTag } from '@/components/MobileApproval/ApprovalWirte';
import { useTranslation } from 'react-i18next';
import { StyledSVGButton } from '@/styles/GlobalStyles';
import SvgImage from '@/components/Module/SvgImage';
import { Box, Stack } from '@mui/material';
import { WorkEventName, WorkPageAreaType } from '@/constant/gtm';
import { useWorkEventTracker } from '@/hooks/gtm/useGTMEventTracker';
import { ApprovalTypeByTag } from '@/components/Approval/CreateApprovalModal/SelectApprovalType';
import ApprovalCheckBox from '@/components/Approval/ApprovalCategoryFilters/ApprovalCheckBox';

type P = {
  pageArea: WorkPageAreaType;
  handleClose(): void;
};

const ApprovalCategoryFilters = ({ pageArea, handleClose }: P) => {
  const { t } = useTranslation();
  const workEventTracker = useWorkEventTracker(pageArea);
  const { data } = useGetApprovalAllType();
  const categorizeByTagData = categorizeByTag(data);
  const approvalTags = Object.entries(categorizeByTagData).map(
    ([key, value]) => ({
      menu: key,
      list: value,
    }),
  );

  const methods = useForm<{ keyword: string }>({
    defaultValues: { keyword: '' },
  });
  const searchKeyword = methods.watch('keyword');
  const debouncedKeyword = useDebounce(searchKeyword, 500);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const filteredApprovalTags = useMemo(() => {
    const tag = !selectedIndex
      ? approvalTags.slice(1)
      : [approvalTags[selectedIndex]];

    if (!debouncedKeyword) return tag;

    return tag.reduce((prev, _tag) => {
      const list = _tag.list.filter((item) => {
        return item.name
          .toUpperCase()
          .replaceAll(' ', '')
          .includes(debouncedKeyword.toUpperCase());
      });

      if (list.length > 0) prev.push({ menu: _tag.menu, list });

      return prev;
    }, [] as ApprovalTypeByTag[]);
  }, [approvalTags, selectedIndex, debouncedKeyword]);

  useEffect(() => {
    if (!debouncedKeyword) return;
    // GA Work - Approval 31번
    workEventTracker(WorkEventName.searchKeyword, {
      area_name: '양식_필터',
      keyword: debouncedKeyword,
    });
  }, [debouncedKeyword]);

  const handleClickTag = (id: number) => {
    const tag = approvalTags[id];
    if (!tag) return;

    setSelectedIndex(id);

    // GA Work - Approval 30번
    workEventTracker(WorkEventName.clickEvent, {
      area_name: '양식_필터',
      contents: tag.menu,
      status: true,
      description: '품의 양식 카테고리 선택',
    });
  };

  return (
    <Box sx={{ overflowY: 'scroll' }}>
      <div style={{ position: 'sticky', top: 0, backgroundColor: 'var(--dk-bg, #fff)' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: '32px',
          }}
        >
          <div style={{ fontSize: '24px', fontWeight: '500' }}>{t('양식')}</div>
          <StyledSVGButton onClick={handleClose}>
            <SvgImage name={'approval_close'} />
          </StyledSVGButton>
        </div>
        <div
          style={{
            height: '1px',
            width: '100%',
            backgroundColor: 'black',
            marginTop: '8px',
          }}
        />
        <div
          style={{
            display: 'flex',
            overflowX: 'auto',
            gap: '8px',
            marginTop: '20px',
          }}
        >
          {approvalTags.map(({ menu }, idx) => (
            <SubHeaderContent
              key={`menuList-${menu}`}
              id={idx}
              title={menu}
              clicked={selectedIndex === idx}
              handleClick={handleClickTag}
              style={{ padding: '8px 16px', fontSize: '16px' }}
              darkMode
            />
          ))}
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '12px',
          }}
        >
          <div />
          <FormProvider {...methods}>
            <CustomInput
              width={'330px'}
              placeHolder={t('양식명을 검색해 주세요.')}
            />
          </FormProvider>
        </div>
      </div>
      <Stack direction="row" flexWrap={'wrap'} sx={{ marginTop: '20px' }}>
        {filteredApprovalTags.length > 0
          ? filteredApprovalTags.map(({ list }) => {
              return list.map((item) => (
                <ApprovalCheckBox
                  key={item.type}
                  pageArea={pageArea}
                  content={item}
                />
              ));
            })
          : approvalTags.length > 0 && (
              <NoData
                title={
                  <>
                    {t('검색어와 일치하는 문서가 없어요.')}
                    <br />
                    {t('검색어를 다시 한번 확인해 주세요.')}
                  </>
                }
              />
            )}
      </Stack>
    </Box>
  );
};

export default ApprovalCategoryFilters;
